<template>
  <div>
    <div class="d-flex">
      <nav id="breadcrumbs" class="mb-3">
        <ul>
          <li>
            <router-link :to="{ name: 'company.dashboard' }">
              <a href="#"> <i class="uil-home-alt"></i> </a>
            </router-link>
          </li>
          <li>
            <span>{{ $t("report.company_dashboard.reports") }}</span>
          </li>
          <li>{{ $t("report.user_login_report") }}</li>
        </ul>
      </nav>
    </div>

    <div class="section-header mb-lg-2 border-0 uk-flex-middle">
      <div class="section-header-left">
        <h2 class="uk-heading-line text-left">
          <span>{{ $t("report.user_login_report") }}</span>
        </h2>
      </div>
    </div>

    <div class="card">
      <div class="card-header actions-toolbar border-0">
        <div class="d-flex justify-content-between align-items-center">
          <div class="row" style="width: 100%">
            <div class="col-md-1 search-icon">
              <i class="uil-search"></i>
            </div>
            <div class="col-md-11 uk-search uk-search-navbar" style="margin-left: -2rem">
              <input
                  v-model="filter.search"
                  :placeholder="$t('general.search')"
                  autofocus
                  class="uk-search-input shadow-0 uk-form-small"
                  type="search"
              />
            </div>
          </div>

          <div class="d-flex">
            <sort-dropdown :sort-options="sortOption" @sort-selected="sortOrder"/>

            <a :uk-tooltip="$t('general.filter_list')"
               aria-expanded="false"
               class="btn btn-icon btn-hover btn-circle"
               href="javascript:void(0)"
            >
              <i class="uil-filter"></i>
            </a>
            <DateFilterModal v-model="selectedDate"
                             modal-id="user-login-date-filter-modal"
                             @clear="clearFilters"
                             @filter="applyFilters"
            />

            <excel-download-button :loading="isReportDownloading" :tooltipText="$t('filters.export_excell')"
                                   @download="exportUserLogins"/>
          </div>
        </div>
      </div>

      <default-loading v-if="user_loading"></default-loading>
      <template v-else>
        <div class="table-responsive">
          <table class="table align-items-center">
            <thead>
            <tr>
              <th scope="col">{{ $t("users.user_email") }}</th>
              <th scope="col">{{ $t("general.first_name_and_last_name") }}</th>
              <th scope="col">{{ $t("profile.corporate") }}</th>
              <th scope="col">{{ $t("report.login_date") }}</th>
            </tr>
            </thead>
            <tbody class="list">
            <tr v-for="item in formattedItems" :key="item.id">
              <td>{{ item.email }}</td>
              <td>{{ item.fullName }}</td>
              <td>{{ item.corporateName }}</td>
              <td>{{ item.loginDate }}</td>
            </tr>
            </tbody>
          </table>
        </div>
        <pagination :pagination="pagination" class="mt-2 mb-3" @changePage="setPage($event)"></pagination>
      </template>
    </div>
  </div>
</template>

<script>
import reports, {
  ERROR,
  GET_ITEMS,
  ITEMS,
  LOADING,
  MODULE_NAME,
  SUCCESS,
} from "@/core/services/store/reports.module";
import store from "@/core/services";
import axios from "axios";
import moment from "moment-timezone";
import "moment-timezone/data/packed/latest.json";
import DefaultLoading from "@/view/components/loading/DefaultLoading";
import Pagination from "@/assets/components/Pagination";
import ExcelDownloadButton from "@/view/components/buttons/ExcelDownloadButton.vue";
import SortDropdown from "@/view/components/dropdown/SortDropdown.vue";
import DateFilterModal from "@/view/components/modal/DateFilterModal.vue";
import { showError } from "@/utils/alertUtils";
import { convertUTCToLocal } from "@/utils/timeUtils";

const _MODULE_NAME = MODULE_NAME;

export default {
  name: "UserLoginReport",
  beforeCreate() {
    function registerStoreModule(moduleName, storeModule) {
      if (!(store && store.state && store.state[moduleName])) {
        store.registerModule(moduleName, storeModule);
      }
    }

    registerStoreModule(_MODULE_NAME, reports);
  },
  components: {
    DefaultLoading,
    ExcelDownloadButton,
    Pagination,
    SortDropdown,
    DateFilterModal,
  },
  data() {
    return {
      pagination: null,
      page: 1,
      per_page: 10,
      formattedItems: [],
      from_date: null,
      to_date: null,
      selectedDate: null,
      searchDebounceTimeout: null,
      isReportDownloading: false,
      sortOption: [
        { key: "text-asc", sort: "fullname", order: "asc", label: this.$t("sorting.a_z") },
        { key: "text-desc", sort: "fullname", order: "desc", label: this.$t("sorting.z_a") },
        { key: "created_at-asc", sort: "created_at", order: "asc", label: this.$t("sorting.created_at_old") },
        { key: "created_at-desc", sort: "created_at", order: "desc", label: this.$t("sorting.created_at_new") },
      ],
      filter: {
        search: null,
        order: "desc",
        sort: "created_at",
      },
    };
  },
  computed: {
    items() {
      return store.getters[_MODULE_NAME + "/" + ITEMS];
    },
    user_loading() {
      return store.getters[_MODULE_NAME + "/" + LOADING];
    },
    errors() {
      return store.getters[_MODULE_NAME + "/" + ERROR];
    },
    success() {
      return store.getters[_MODULE_NAME + "/" + SUCCESS];
    },
  },
  methods: {
    buildFilters() {
      const filters = {
        like: this.filter.search,
        page: this.page,
        per_page: this.per_page,
        sort: this.filter.sort,
        order: this.filter.order,
        from_date: this.from_date,
        to_date: this.to_date,
      };

      Object.keys(filters).forEach((key) => {
        if (filters[key] === null || filters[key] === undefined || filters[key] === "") {
          delete filters[key];
        }
      });

      return filters;
    },
    exportUserLogins() {
      if (this.isReportDownloading) {
        return;
      }

      this.isReportDownloading = true;

      axios
          .get(`${process.env.VUE_APP_BACKEND_APP_URL}/api/report/export/user-logins`, {
            params: {
              ...this.buildFilters(),
              report: Math.random() * 9999999,
            },
            responseType: "blob",
            headers: this.apiHeaders,
          })
          .then((response) => {
            const fileURL = window.URL.createObjectURL(new Blob([response.data]));
            const fileLink = document.createElement("a");
            const day = moment().format("YYYYMMDDHHmmss");

            fileLink.href = fileURL;
            fileLink.setAttribute("download", `user_login_report_list_${day}.xlsx`);
            document.body.appendChild(fileLink);
            fileLink.click();
            document.body.removeChild(fileLink);
            window.URL.revokeObjectURL(fileURL);
            this.isReportDownloading = false;
          })
          .catch((error) => {
            this.isReportDownloading = false;
            showError(error);
          });
    },
    filterResult() {
      store
          .dispatch(_MODULE_NAME + "/" + GET_ITEMS, {
            url: "api/report/user-logins",
            filters: this.buildFilters(),
          })
          .then((response) => {
            if (response && response.status) {
              this.pagination = response.data;
              this.formattedItems = response.data.data.map((item) => ({
                id: item.id,
                email: item.email ?? "-",
                fullName: item.fullName || "-",
                corporateName: item.corporateName || "-",
                loginDate: convertUTCToLocal(item.loginDate) || "-",
              }));
            }
          });
    },
    sortOrder({ order, sort }) {
      this.page = 1;
      this.filter.order = order;
      this.filter.sort = sort;
      this.filterResult();
    },
    setPage(page) {
      this.page = page;
      this.filterResult();
    },
    applyFilters(payload = {}) {
      this.page = 1;
      this.selectedDate = payload.selectedDate ?? this.selectedDate;
      this.from_date = payload.from_date ?? null;
      this.to_date = payload.to_date ?? null;
      this.filterResult();
    },
    clearFilters() {
      this.page = 1;
      this.selectedDate = null;
      this.from_date = null;
      this.to_date = null;
      this.filterResult();
    },
  },
  mounted() {
    this.filterResult();
  },
  watch: {
    success(value) {
      if (value) {
        this.filterResult();
      }
    },
    "filter.search"() {
      clearTimeout(this.searchDebounceTimeout);
      this.searchDebounceTimeout = setTimeout(() => {
        this.page = 1;
        this.filterResult();
      }, 500);
    },
  },
  beforeDestroy() {
    clearTimeout(this.searchDebounceTimeout);
  },
};
</script>
